import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@mui/material';
import { useAuth } from 'src/hooks/use-auth';
import Globe01Icon from '@untitled-ui/icons-react/build/esm/Globe01';
import BarChartSquare02Icon from 'src/icons/untitled-ui/duocolor/bar-chart-square-02';
import Building04Icon from 'src/icons/untitled-ui/duocolor/building-04';
import CalendarIcon from 'src/icons/untitled-ui/duocolor/calendar';
import CreditCard01Icon from 'src/icons/untitled-ui/duocolor/credit-card-01';
import File01Icon from 'src/icons/untitled-ui/duocolor/file-01';
import GraduationHat01Icon from 'src/icons/untitled-ui/duocolor/graduation-hat-01';
import HomeSmileIcon from 'src/icons/untitled-ui/duocolor/home-smile';
import Mail03Icon from 'src/icons/untitled-ui/duocolor/mail-03';
import MessageChatSquareIcon from 'src/icons/untitled-ui/duocolor/message-chat-square';
import Share07Icon from 'src/icons/untitled-ui/duocolor/share-07';
import ShoppingCart01Icon from 'src/icons/untitled-ui/duocolor/shopping-cart-01';
import Upload04Icon from 'src/icons/untitled-ui/duocolor/upload-04';
import Users03Icon from 'src/icons/untitled-ui/duocolor/users-03';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import RouteIcon from '@mui/icons-material/Route';
import CampaignIcon from '@mui/icons-material/Campaign';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HandshakeIcon from '@mui/icons-material/Handshake';
import BuildIcon from '@mui/icons-material/Build';
import PeopleIcon from '@mui/icons-material/People';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import InsightsIcon from '@mui/icons-material/Insights';
import StorageIcon from '@mui/icons-material/Storage';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VideocamIcon from '@mui/icons-material/Videocam';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TuneIcon from '@mui/icons-material/Tune';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChatIcon from '@mui/icons-material/Chat';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CellTowerIcon from '@mui/icons-material/CellTower';
import DevicesIcon from '@mui/icons-material/Devices';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MemoryIcon from '@mui/icons-material/Memory';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import BugReportIcon from '@mui/icons-material/BugReport';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PhoneIcon from '@mui/icons-material/Phone';
import SmsIcon from '@mui/icons-material/Sms';
import { paths } from 'src/paths';

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
  titleColor?: string;
}

export interface Section {
  items: Item[];
  subheader?: string;
  icon?: ReactNode;
  singleLink?: boolean;
  path?: string;
}

export const useSections = (): Section[] => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.role || 'admin';

  return useMemo(
    () => {
      const allSections: Section[] = [
        {
          subheader: 'Overview',
          singleLink: true,
          path: paths.dashboard.index,
          icon: (
            <SvgIcon fontSize="small">
              <HomeSmileIcon />
            </SvgIcon>
          ),
          items: []
        },
        {
          subheader: 'Cloud',
          singleLink: true,
          path: paths.dashboard.cloud,
          icon: (
            <SvgIcon fontSize="small">
              <Upload04Icon />
            </SvgIcon>
          ),
          items: []
        },
        {
          subheader: 'Management',
          icon: (
            <SvgIcon fontSize="small">
              <BusinessCenterIcon />
            </SvgIcon>
          ),
          items: [
            {
              title: 'Calendar',
              path: paths.dashboard.calendar,
              icon: (
                <SvgIcon fontSize="small">
                  <CalendarIcon />
                </SvgIcon>
              )
            },
            {
              title: 'Contractors',
              path: paths.dashboard.contractors,
              icon: (
                <SvgIcon fontSize="small">
                  <Users03Icon />
                </SvgIcon>
              )
            },
            {
              title: 'Inventory',
              path: paths.dashboard.inventory,
              icon: (
                <SvgIcon fontSize="small">
                  <ShoppingCart01Icon />
                </SvgIcon>
              )
            },
            {
              title: 'Analytics',
              path: paths.dashboard.analytics,
              icon: (
                <SvgIcon fontSize="small">
                  <BarChartSquare02Icon />
                </SvgIcon>
              )
            },
            {
              title: 'Insights',
              path: paths.dashboard.insights,
              icon: (
                <SvgIcon fontSize="small">
                  <InsightsIcon />
                </SvgIcon>
              )
            }
          ]
        },
        {
          subheader: 'CRM',
          icon: (
            <SvgIcon fontSize="small">
              <PeopleIcon />
            </SvgIcon>
          ),
          items: [
            {
              title: 'Leads',
              path: paths.dashboard.customers.index,
              icon: (
                <SvgIcon fontSize="small">
                  <Users03Icon />
                </SvgIcon>
              )
            },
            {
              title: 'Documents',
              path: paths.dashboard.documents,
              icon: (
                <SvgIcon fontSize="small">
                  <FolderOpenIcon />
                </SvgIcon>
              )
            },
            {
              title: 'Enrichment',
              path: paths.dashboard.enrichment,
              icon: (
                <SvgIcon fontSize="small">
                  <PersonSearchIcon />
                </SvgIcon>
              )
            }
          ]
        },
        {
          subheader: 'Outbound',
          icon: (
            <SvgIcon fontSize="small">
              <Mail03Icon />
            </SvgIcon>
          ),
          items: [
            {
              title: 'Create',
              path: paths.dashboard.create,
              icon: (
                <SvgIcon fontSize="small">
                  <AddCircleOutlineIcon />
                </SvgIcon>
              )
            },
            {
              title: 'Inbox',
              path: paths.dashboard.inbox,
              icon: (
                <SvgIcon fontSize="small">
                  <Mail03Icon />
                </SvgIcon>
              )
            },
          ]
        },
        {
          subheader: 'Marketing',
          icon: (
            <SvgIcon fontSize="small">
              <CampaignIcon />
            </SvgIcon>
          ),
          items: [
            {
              title: 'Campaigns',
              path: paths.dashboard.marketing.campaigns,
              icon: (
                <SvgIcon fontSize="small">
                  <Share07Icon />
                </SvgIcon>
              )
            },
            {
              title: 'Social Media',
              path: paths.dashboard.marketing.socialMedia,
              icon: (
                <SvgIcon fontSize="small">
                  <MessageChatSquareIcon />
                </SvgIcon>
              )
            }
          ]
        },
        {
          subheader: 'Sales',
          icon: (
            <SvgIcon fontSize="small">
              <AttachMoneyIcon />
            </SvgIcon>
          ),
          items: [
            {
              title: 'Sales Dashboard',
              path: paths.dashboard.salesDashboard,
              icon: (
                <SvgIcon fontSize="small">
                  <BarChartSquare02Icon />
                </SvgIcon>
              )
            },
            {
              title: 'Sales Pipeline',
              path: paths.dashboard.salesPipeline,
              icon: (
                <SvgIcon fontSize="small">
                  <RouteIcon />
                </SvgIcon>
              )
            },
            {
              title: 'Sales Recordings',
              path: paths.dashboard.salesRecordings,
              icon: (
                <SvgIcon fontSize="small">
                  <MessageChatSquareIcon />
                </SvgIcon>
              )
            },
            {
              title: 'Recording Tool',
              path: paths.dashboard.recordingTool,
              icon: (
                <SvgIcon fontSize="small">
                  <VideocamIcon />
                </SvgIcon>
              )
            },
            {
              title: 'Training',
              path: paths.dashboard.training,
              icon: (
                <SvgIcon fontSize="small">
                  <GraduationHat01Icon />
                </SvgIcon>
              )
            },
            {
              title: 'Scripts',
              path: paths.dashboard.salesScripts,
              icon: (
                <SvgIcon fontSize="small">
                  <File01Icon />
                </SvgIcon>
              )
            }
          ]
        },
        {
          subheader: 'Support',
          singleLink: true,
          path: paths.dashboard.supportHub,
          icon: (
            <SvgIcon fontSize="small">
              <SupportAgentIcon />
            </SvgIcon>
          ),
          items: []
        },
        {
          subheader: 'Integration',
          icon: (
            <SvgIcon fontSize="small">
              <InsightsIcon />
            </SvgIcon>
          ),
          items: [
            {
              title: 'Tools',
              path: paths.dashboard.tools,
              icon: (
                <SvgIcon fontSize="small">
                  <BuildIcon />
                </SvgIcon>
              )
            },
            {
              title: 'Workflows',
              path: paths.dashboard.workflows,
              icon: (
                <SvgIcon fontSize="small">
                  <RouteIcon />
                </SvgIcon>
              )
            },
            {
              title: 'Database Moat',
              path: paths.dashboard.databaseMoat,
              icon: (
                <SvgIcon fontSize="small">
                  <StorageIcon />
                </SvgIcon>
              )
            },
            {
              title: 'Knowledge Base',
              path: paths.dashboard.knowledgeBase,
              icon: (
                <SvgIcon fontSize="small">
                  <AutoStoriesIcon />
                </SvgIcon>
              )
            }
          ]
        },
        {
          subheader: 'Logs',
          icon: (
            <SvgIcon fontSize="small">
              <AssignmentIcon />
            </SvgIcon>
          ),
          singleLink: true,
          path: paths.dashboard.logs,
          items: [
            {
              title: 'Logs',
              path: paths.dashboard.logs,
              icon: (
                <SvgIcon fontSize="small">
                  <AssignmentIcon />
                </SvgIcon>
              )
            }
          ]
        },
        {
          subheader: 'Settings',
          icon: (
            <SvgIcon fontSize="small">
              <SettingsIcon />
            </SvgIcon>
          ),
          singleLink: true,
          path: paths.dashboard.settings,
          items: [
            {
              title: 'Settings',
              path: paths.dashboard.settings,
              icon: (
                <SvgIcon fontSize="small">
                  <SettingsIcon />
                </SvgIcon>
              )
            }
          ]
        },
      ];

      // Salesman: explicit ordered nav — Inbox, Calendar, Leads, Pipeline, Tasks, Logs
      if (role === 'salesman') {
        return [
          {
            subheader: '',
            singleLink: true,
            path: paths.dashboard.inbox,
            icon: (
              <SvgIcon fontSize="small">
                <Mail03Icon />
              </SvgIcon>
            ),
            items: [],
          },
          {
            subheader: '',
            singleLink: true,
            path: paths.dashboard.calendar,
            icon: (
              <SvgIcon fontSize="small">
                <CalendarIcon />
              </SvgIcon>
            ),
            items: [],
          },
          {
            subheader: '',
            singleLink: true,
            path: paths.dashboard.customers.index,
            icon: (
              <SvgIcon fontSize="small">
                <Users03Icon />
              </SvgIcon>
            ),
            items: [],
          },
          {
            subheader: '',
            singleLink: true,
            path: paths.dashboard.salesPipeline,
            icon: (
              <SvgIcon fontSize="small">
                <RouteIcon />
              </SvgIcon>
            ),
            items: [],
          },
          {
            subheader: '',
            singleLink: true,
            path: paths.dashboard.tasks,
            icon: (
              <SvgIcon fontSize="small">
                <TuneIcon />
              </SvgIcon>
            ),
            items: [],
          },
          {
            subheader: '',
            singleLink: true,
            path: paths.dashboard.logs,
            icon: (
              <SvgIcon fontSize="small">
                <AssignmentIcon />
              </SvgIcon>
            ),
            items: [],
          },
        ];
      }

      return allSections;
    },
    [t, role]
  );
};
